<script lang="ts" setup>
import OmniDialog from "@/components/OmniDialog.vue";
import type { TripRouteDto } from "@/api/trips/dto/trip.dto";
import RouteShortInfo from "@/views/trips/view/components/RouteShortInfo.vue";
import { computed } from "vue";
import { RouteStatus } from "@/data/trip";
import TruckLink from "@/components/links/TruckLink.vue";

const model = defineModel<boolean>({ default: false });

const props = defineProps<{
  routes: TripRouteDto[];
}>();

const emits = defineEmits<{
  (e: "onClose"): void;
}>();

const routesByTruck = computed<Record<string, TripRouteDto[]>>(() => {
  const result = props.routes.reduce<Record<string, TripRouteDto[]>>((acc, item) => {
    if (!item.truckId) {
      return acc;
    }

    const truckIdString = String(item.truckId);

    if (acc[truckIdString]) {
      acc[truckIdString].push(item);
    } else {
      acc[truckIdString] = [item];
    }

    return acc;
  }, {});

  return result;
});

const uniqueTruckId = computed(() => {
  return Object.keys(routesByTruck.value);
});

function getNextCurrentRoutes(truckId: string) {
  const truckRoutes = routesByTruck.value[truckId];

  return truckRoutes.filter(
    (truckRoute) => truckRoute.status === RouteStatus.notStarted || truckRoute.status === RouteStatus.inProgress,
  );
}

function getPlannedRoutes(truckId: string) {
  const truckRoutes = routesByTruck.value[truckId];

  return truckRoutes?.filter((truckRoute) => truckRoute.status === RouteStatus.planned);
}

function onModelChanged(value: boolean) {
  if (!value) {
    emits("onClose");
  }
}
</script>

<template>
  <omni-dialog v-model="model" title="" width="950" @update:model-value="onModelChanged">
    <div class="d-flex justify-center">
      <v-icon color="success" size="56">mdi-check</v-icon>
    </div>

    <div class="my-6">
      <slot name="header"></slot>
    </div>

    <div v-for="truckId of uniqueTruckId" :key="truckId" class="route">
      <div class="d-flex justify-space-between">
        <span>Routes for <truck-link :truck-id="truckId" :truck-number="truckId" /></span>

        <router-link
          :to="{ name: 'truck-view', params: { id: truckId }, hash: '#active-routes' }"
          class="text-primary text-decoration-none font-weight-500"
          target="_blank"
        >
          View planned routes
        </router-link>
      </div>

      <div class="mt-3">
        <div v-if="getNextCurrentRoutes(truckId)?.length">
          <div class="text-grey-darken-2">Current route:</div>
          <route-short-info
            v-for="(item, index) of getNextCurrentRoutes(truckId)"
            :key="item.id"
            :index="index"
            :route="item"
            class="mt-4"
            data-qa="active-route-block"
          ></route-short-info>
        </div>

        <div v-if="getPlannedRoutes(truckId)?.length" class="mt-3">
          <div class="text-grey-darken-2">Planned route(s):</div>
          <route-short-info
            v-for="(item, index) of getPlannedRoutes(truckId)"
            :key="item.id"
            :index="index"
            :route="item"
            class="mt-4"
            data-qa="planned-route-block"
          ></route-short-info>
        </div>
      </div>
    </div>
  </omni-dialog>
</template>

<style lang="scss" scoped>
@import "@/assets/style/color";

.route {
  margin-top: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid $grey-light;

  &:last-child {
    border-bottom: none;
  }
}
</style>
